import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Search, Trash2, Edit, ArrowLeft, Building2, MapPin, Star, Users, X, Save, Navigation } from "lucide-react";
import { api, errMsg } from "@/lib/config";

const DIVISIONS = [
  { key: "elettrico", label: "Impianti Elettrici" },
  { key: "automazioni", label: "Automazioni" },
  { key: "trattamento-acqua", label: "Trattamento Acque" },
  { key: "fotovoltaico", label: "Fotovoltaico" },
  { key: "antincendio", label: "Antincendio" },
  { key: "irrigazione", label: "Irrigazione" },
  { key: "generic", label: "Generale" },
];

// Compose full address string from possibly-empty fragments.
const buildAddress = (...parts) => parts.map((p) => (p || "").trim()).filter(Boolean).join(", ");

// ─── Reusable Address Map + Navigate button ─────────────────
function AddressMap({ address, label }) {
  if (!address) return null;
  const q = encodeURIComponent(address);
  const embedUrl = `https://www.google.com/maps?q=${q}&z=16&hl=it&output=embed`;
  // Directions URL: opens native Maps app on iOS/Android, Google Maps on desktop
  const navUrl = `https://www.google.com/maps/dir/?api=1&destination=${q}`;
  return (
    <div className="mt-4 border border-[#1a1a1a] bg-[#0a0a0a] overflow-hidden">
      <div className="px-4 py-3 border-b border-[#1a1a1a] flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <MapPin size={14} className="text-[#ff4b00] shrink-0" />
          <div className="min-w-0">
            {label && <div className="font-mono-tech text-[10px] text-white">{label}</div>}
            <div className="text-white text-sm truncate">{address}</div>
          </div>
        </div>
        <a
          href={navUrl}
          target="_blank"
          rel="noreferrer"
          className="btn-primary shrink-0 text-[11px]"
          data-testid="address-navigate"
        >
          <Navigation size={12} /> Naviga
        </a>
      </div>
      <iframe
        title={`Mappa ${label || "indirizzo"}`}
        src={embedUrl}
        width="100%"
        height="240"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        style={{ border: 0, filter: "grayscale(0.4) contrast(1.05) brightness(0.9)" }}
      />
    </div>
  );
}

export default function AnagraficaPanel() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // customer id being edited, or "new"

  const load = async (query = "") => {
    setLoading(true);
    try {
      const r = await api.get("/admin/customers", { params: query ? { q: query } : {} });
      setCustomers(r.data || []);
    } catch (e) { toast.error("Errore nel caricamento"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => load(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const remove = async (c) => {
    if (!window.confirm(`Eliminare il cliente "${c.name}" e tutti i suoi impianti? Operazione irreversibile.`)) return;
    try {
      await api.delete(`/admin/customers/${c.id}`);
      toast.success("Cliente eliminato");
      await load(q);
    } catch (e) { toast.error(errMsg(e, "Errore")); }
  };

  if (editing) {
    return <CustomerDetail
      customerId={editing === "new" ? null : editing}
      onClose={() => { setEditing(null); load(q); }}
    />;
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-8 gap-6">
        <div>
          <div className="num-chip mb-3"><span className="dot" /> Registro clienti</div>
          <div className="font-display text-3xl md:text-4xl">Anagrafica</div>
          <div className="text-white text-sm mt-2 max-w-xl">Gestisci l&apos;archivio clienti con referenti multipli e impianti installati. Base per rapporti di intervento e contratti.</div>
        </div>
        <button onClick={() => setEditing("new")} data-testid="anagrafica-new" className="btn-primary shrink-0">
          <Plus size={14} /> Nuovo cliente
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca per nome, ragione sociale, email, P.IVA..."
          className="field w-full pl-10"
          data-testid="anagrafica-search"
        />
      </div>

      {loading ? (
        <div className="font-mono-tech text-white py-16 text-center flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Caricamento...</div>
      ) : customers.length === 0 ? (
        <div className="border border-dashed border-[#222] p-16 text-center bg-[#0a0a0a]/50">
          <Building2 size={32} className="text-white mx-auto mb-4" />
          <div className="font-display text-2xl text-white mb-2">
            {q ? `Nessun cliente trovato per "${q}"` : "Nessun cliente ancora"}
          </div>
          {!q && (
            <>
              <div className="font-mono-tech text-[11px] text-white mb-6">Inizia creando la scheda del primo cliente.</div>
              <button onClick={() => setEditing("new")} className="btn-primary inline-flex">Crea cliente</button>
            </>
          )}
        </div>
      ) : (
        <div className="border border-[#1a1a1a] overflow-x-auto">
          <table className="w-full text-left font-mono-tech text-[11px] min-w-[900px]" data-testid="anagrafica-table">
            <thead className="bg-[#0a0a0a] text-white">
              <tr>
                <th className="p-3">Cliente</th>
                <th className="p-3">Ragione sociale</th>
                <th className="p-3">P.IVA</th>
                <th className="p-3">Città</th>
                <th className="p-3">Email primaria</th>
                <th className="p-3">Referenti</th>
                <th className="p-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c.id} className="border-t border-[#151515] hover:bg-[#0a0a0a]" data-testid={`anagrafica-row-${c.id}`}>
                  <td className="p-3 text-white">{c.name}</td>
                  <td className="p-3 text-white">{c.ragione_sociale || c.company || "—"}</td>
                  <td className="p-3 text-white">{c.vat || "—"}</td>
                  <td className="p-3 text-white">{c.city || "—"}</td>
                  <td className="p-3 text-white">{c.email}</td>
                  <td className="p-3 text-white">{(c.referenti || []).length}</td>
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => setEditing(c.id)} title="Modifica" className="p-1.5 text-white hover:text-[#ff4b00] border border-[#1a1a1a]" data-testid={`anagrafica-edit-${c.id}`}><Edit size={12} /></button>
                      <button onClick={() => remove(c)} title="Elimina" className="p-1.5 text-white hover:text-red-400 border border-[#1a1a1a]" data-testid={`anagrafica-delete-${c.id}`}><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CustomerDetail({ customerId, onClose }) {
  const isNew = !customerId;
  const [tab, setTab] = useState("dati");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [f, setF] = useState({
    email: "", name: "",
    ragione_sociale: "", vat: "", codice_fiscale: "",
    address_line: "", city: "", province: "", cap: "",
    phone: "", pec: "", website: "", notes: "",
    referenti: [], // {id, name, email, phone, role, is_primary}
  });
  const [plants, setPlants] = useState([]);
  const [plantsLoading, setPlantsLoading] = useState(false);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    api.get(`/admin/customers/${customerId}`)
      .then((r) => setF({
        ...r.data,
        referenti: r.data.referenti || [],
      }))
      .catch(() => toast.error("Errore nel caricamento"))
      .finally(() => setLoading(false));
  }, [customerId, isNew]);

  const loadPlants = async () => {
    if (isNew) return;
    setPlantsLoading(true);
    try {
      const r = await api.get("/admin/plants", { params: { customer_id: customerId } });
      setPlants(r.data);
    } catch (e) { toast.error("Errore caricamento impianti"); }
    finally { setPlantsLoading(false); }
  };
  useEffect(() => { if (tab === "impianti" && !isNew) loadPlants(); }, [tab, customerId, isNew]);

  const save = async () => {
    if (!f.email || !f.name) { toast.error("Nome ed email obbligatori"); return; }
    setSaving(true);
    try {
      // Filter out empty referenti (avoid backend 422 if user added a row and left it empty)
      const cleanReferenti = (f.referenti || []).filter((r) => r.name && r.name.trim());
      const payload = { ...f, referenti: cleanReferenti };
      if (isNew) {
        const r = await api.post("/admin/customers", payload);
        toast.success("Cliente creato");
        setF({ ...r.data, referenti: r.data.referenti || [] });
        onClose();
      } else {
        await api.put(`/admin/customers/${customerId}`, payload);
        toast.success("Cliente aggiornato");
      }
    } catch (e) {
      toast.error(errMsg(e, "Errore nel salvataggio"));
    } finally { setSaving(false); }
  };

  const addReferente = () => {
    setF({ ...f, referenti: [...f.referenti, { id: `new-${Date.now()}`, name: "", email: "", phone: "", role: "", is_primary: f.referenti.length === 0 }] });
  };
  const updateReferente = (idx, updates) => {
    const arr = [...f.referenti];
    if (updates.is_primary) {
      arr.forEach((r, i) => { r.is_primary = i === idx; });
    } else {
      arr[idx] = { ...arr[idx], ...updates };
    }
    setF({ ...f, referenti: arr });
  };
  const removeReferente = (idx) => {
    const arr = f.referenti.filter((_, i) => i !== idx);
    if (arr.length > 0 && !arr.some((r) => r.is_primary)) arr[0].is_primary = true;
    setF({ ...f, referenti: arr });
  };

  if (loading) return <div className="py-16 text-center font-mono-tech text-white"><Loader2 className="animate-spin inline mr-2" size={16} /> Caricamento cliente...</div>;

  const TABS = [
    { k: "dati", label: "Dati", icon: Building2 },
    { k: "referenti", label: `Referenti (${f.referenti.length})`, icon: Users, disabled: false },
    { k: "impianti", label: `Impianti (${plants.length})`, icon: MapPin, disabled: isNew },
  ];

  return (
    <div>
      <button onClick={onClose} className="font-mono-tech text-[10px] text-white hover:text-[#ff4b00] flex items-center gap-2 mb-6" data-testid="anagrafica-back">
        <ArrowLeft size={12} /> Torna all&apos;anagrafica
      </button>

      <div className="flex items-start justify-between mb-8 gap-6">
        <div>
          <div className="num-chip mb-3"><span className="dot" /> {isNew ? "Nuovo cliente" : "Scheda cliente"}</div>
          <div className="font-display text-3xl md:text-4xl">{isNew ? "Crea cliente" : (f.name || "Cliente")}</div>
          {!isNew && f.ragione_sociale && <div className="font-mono-tech text-[11px] text-white mt-2">{f.ragione_sociale} · P.IVA {f.vat || "—"}</div>}
        </div>
        <button onClick={save} disabled={saving} data-testid="anagrafica-save" className="btn-primary shrink-0">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? "Salvataggio..." : (isNew ? "Crea cliente" : "Salva modifiche")}
        </button>
      </div>

      {/* Sub-tabs */}
      <div className="border-b border-[#1a1a1a] mb-6 flex gap-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.k}
              onClick={() => !t.disabled && setTab(t.k)}
              disabled={t.disabled}
              data-testid={`anagrafica-subtab-${t.k}`}
              className={`font-mono-tech text-[11px] px-4 py-3 border-b-2 -mb-px flex items-center gap-2 ${tab === t.k ? "border-[#ff4b00] text-[#ff4b00]" : "border-transparent text-white hover:text-white"} ${t.disabled ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              <Icon size={12} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "dati" && <DatiTab f={f} setF={setF} />}
      {tab === "referenti" && (
        <ReferentiTab
          referenti={f.referenti}
          onAdd={addReferente}
          onUpdate={updateReferente}
          onRemove={removeReferente}
        />
      )}
      {tab === "impianti" && (
        <ImpiantiTab
          customerId={customerId}
          plants={plants}
          loading={plantsLoading}
          onReload={loadPlants}
        />
      )}
    </div>
  );
}

function DatiTab({ f, setF }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
      <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="field-label">Nome / Denominazione *</label>
          <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="field mt-2 w-full" placeholder="Es. ACME S.p.A." data-testid="cust-name" />
        </div>
        <div>
          <label className="field-label">Email primaria *</label>
          <input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="field mt-2 w-full" data-testid="cust-email" />
        </div>
      </div>

      <div>
        <label className="field-label">Ragione sociale</label>
        <input value={f.ragione_sociale} onChange={(e) => setF({ ...f, ragione_sociale: e.target.value })} className="field mt-2 w-full" placeholder="Es. ACME Industria S.p.A." />
      </div>
      <div>
        <label className="field-label">Telefono principale</label>
        <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="field mt-2 w-full" />
      </div>

      <div>
        <label className="field-label">P.IVA</label>
        <input value={f.vat} onChange={(e) => setF({ ...f, vat: e.target.value })} className="field mt-2 w-full font-mono-tech" />
      </div>
      <div>
        <label className="field-label">Codice Fiscale</label>
        <input value={f.codice_fiscale} onChange={(e) => setF({ ...f, codice_fiscale: e.target.value })} className="field mt-2 w-full font-mono-tech" />
      </div>

      <div>
        <label className="field-label">PEC</label>
        <input type="email" value={f.pec} onChange={(e) => setF({ ...f, pec: e.target.value })} className="field mt-2 w-full" placeholder="acme@pec.it" />
      </div>
      <div>
        <label className="field-label">Sito web</label>
        <input value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} className="field mt-2 w-full" placeholder="https://" />
      </div>

      <div className="md:col-span-2">
        <label className="field-label">Indirizzo sede</label>
        <input value={f.address_line} onChange={(e) => setF({ ...f, address_line: e.target.value })} className="field mt-2 w-full" placeholder="Via, numero civico" />
      </div>

      <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="field-label">Città</label>
          <input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="field mt-2 w-full" />
        </div>
        <div>
          <label className="field-label">Provincia</label>
          <input value={f.province} onChange={(e) => setF({ ...f, province: e.target.value })} className="field mt-2 w-full font-mono-tech" maxLength={2} placeholder="PN" />
        </div>
        <div>
          <label className="field-label">CAP</label>
          <input value={f.cap} onChange={(e) => setF({ ...f, cap: e.target.value })} className="field mt-2 w-full font-mono-tech" maxLength={5} />
        </div>
      </div>

      <div className="md:col-span-2">
        <label className="field-label">Note interne</label>
        <textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={3} className="field mt-2 w-full" placeholder="Note visibili solo agli operatori" />
      </div>

      {/* Map + Navigate */}
      <div className="md:col-span-2">
        <AddressMap
          address={buildAddress(f.address_line, [f.cap, f.city].filter(Boolean).join(" "), f.province)}
          label="Sede cliente · click Naviga per aprire nell'app mappe"
        />
      </div>
    </div>
  );
}

function ReferentiTab({ referenti, onAdd, onUpdate, onRemove }) {
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-white text-sm">Aggiungi più referenti per gestire i contatti principali (tecnico, amministrativo, direzione).</div>
        <button onClick={onAdd} className="btn-primary" data-testid="referente-add"><Plus size={14} /> Aggiungi referente</button>
      </div>

      {referenti.length === 0 ? (
        <div className="border border-dashed border-[#222] p-8 text-center">
          <Users size={24} className="text-white mx-auto mb-3" />
          <div className="font-mono-tech text-[11px] text-white">Nessun referente. Aggiungi il primo.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {referenti.map((r, idx) => (
            <div key={r.id} className={`border p-5 ${r.is_primary ? "border-[#ff4b00]/40 bg-[#ff4b00]/5" : "border-[#1a1a1a] bg-[#0a0a0a]"}`} data-testid={`referente-row-${idx}`}>
              <div className="flex items-start justify-between mb-4 gap-3">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => onUpdate(idx, { is_primary: true })}
                    title={r.is_primary ? "Referente primario" : "Imposta come primario"}
                    className={`w-8 h-8 border flex items-center justify-center ${r.is_primary ? "border-[#ff4b00] bg-[#ff4b00]/10 text-[#ff4b00]" : "border-[#333] text-white hover:text-[#ff4b00]"}`}
                    data-testid={`referente-primary-${idx}`}
                  >
                    <Star size={14} fill={r.is_primary ? "currentColor" : "none"} />
                  </button>
                  {r.is_primary && <span className="font-mono-tech text-[10px] text-[#ff4b00]">◐ PRIMARIO</span>}
                </div>
                <button type="button" onClick={() => onRemove(idx)} className="p-1.5 text-white hover:text-red-400" data-testid={`referente-remove-${idx}`}><Trash2 size={12} /></button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="field-label">Nome cognome</label>
                  <input value={r.name} onChange={(e) => onUpdate(idx, { name: e.target.value })} className="field mt-2 w-full" placeholder="Es. Mario Rossi" />
                </div>
                <div>
                  <label className="field-label">Ruolo</label>
                  <input value={r.role || ""} onChange={(e) => onUpdate(idx, { role: e.target.value })} className="field mt-2 w-full" placeholder="Es. Direzione tecnica" />
                </div>
                <div>
                  <label className="field-label">Email</label>
                  <input type="email" value={r.email || ""} onChange={(e) => onUpdate(idx, { email: e.target.value })} className="field mt-2 w-full" />
                </div>
                <div>
                  <label className="field-label">Telefono</label>
                  <input value={r.phone || ""} onChange={(e) => onUpdate(idx, { phone: e.target.value })} className="field mt-2 w-full" />
                </div>
              </div>
            </div>
          ))}
          <div className="font-mono-tech text-[10px] text-white mt-3">◐ Il referente <span className="text-[#ff4b00]">PRIMARIO</span> riceve automaticamente i rapporti di intervento via email. Ricordati di salvare le modifiche.</div>
        </div>
      )}
    </div>
  );
}

function ImpiantiTab({ customerId, plants, loading, onReload }) {
  const [editing, setEditing] = useState(null); // plant id, "new", or null
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({});

  const startNew = () => {
    setF({ customer_id: customerId, name: "", division: "generic", address: "", install_date: "", manufacturer: "", model: "", serial: "", notes: "", photos: [] });
    setEditing("new");
  };

  const startEdit = (p) => { setF(p); setEditing(p.id); };

  const save = async () => {
    if (!f.name.trim()) { toast.error("Nome impianto obbligatorio"); return; }
    setSaving(true);
    try {
      const payload = { ...f, customer_id: customerId };
      if (editing === "new") {
        await api.post("/admin/plants", payload);
        toast.success("Impianto creato");
      } else {
        await api.put(`/admin/plants/${editing}`, payload);
        toast.success("Impianto aggiornato");
      }
      setEditing(null);
      onReload();
    } catch (e) { toast.error(errMsg(e, "Errore")); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare l'impianto "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/plants/${p.id}`);
      toast.success("Impianto eliminato");
      onReload();
    } catch (e) { toast.error(errMsg(e, "Errore")); }
  };

  if (editing) {
    return (
      <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="font-display text-2xl">{editing === "new" ? "Nuovo impianto" : "Modifica impianto"}</div>
          <button onClick={() => setEditing(null)} className="text-white hover:text-white p-2"><X size={16} /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <label className="field-label">Nome impianto *</label>
            <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="field mt-2 w-full" placeholder="Es. Impianto RO Linea 1" data-testid="plant-name" />
          </div>
          <div>
            <label className="field-label">Divisione</label>
            <select value={f.division || "generic"} onChange={(e) => setF({ ...f, division: e.target.value })} className="field mt-2 w-full">
              {DIVISIONS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </div>
          <div>
            <label className="field-label">Data installazione</label>
            <input type="date" value={f.install_date || ""} onChange={(e) => setF({ ...f, install_date: e.target.value })} className="field mt-2 w-full" />
          </div>
          <div className="md:col-span-2">
            <label className="field-label">Indirizzo ubicazione</label>
            <input value={f.address || ""} onChange={(e) => setF({ ...f, address: e.target.value })} className="field mt-2 w-full" placeholder="Se diverso dalla sede cliente" />
          </div>
          <div>
            <label className="field-label">Produttore</label>
            <input value={f.manufacturer || ""} onChange={(e) => setF({ ...f, manufacturer: e.target.value })} className="field mt-2 w-full" placeholder="Es. Grundfos" />
          </div>
          <div>
            <label className="field-label">Modello</label>
            <input value={f.model || ""} onChange={(e) => setF({ ...f, model: e.target.value })} className="field mt-2 w-full" />
          </div>
          <div className="md:col-span-2">
            <label className="field-label">Numero seriale / matricola</label>
            <input value={f.serial || ""} onChange={(e) => setF({ ...f, serial: e.target.value })} className="field mt-2 w-full font-mono-tech" />
          </div>
          <div className="md:col-span-2">
            <label className="field-label">Note tecniche</label>
            <textarea value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={3} className="field mt-2 w-full" placeholder="Es. sostituire filtri ogni 6 mesi" />
          </div>
        </div>
        <div className="mt-6 flex gap-3">
          <button onClick={save} disabled={saving} data-testid="plant-save" className="btn-primary">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? "Salvataggio..." : "Salva impianto"}
          </button>
          <button onClick={() => setEditing(null)} className="btn-ghost">Annulla</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-white text-sm">Registra ogni impianto installato presso il cliente. Sarà selezionabile nei rapporti di intervento.</div>
        <button onClick={startNew} className="btn-primary" data-testid="plant-new"><Plus size={14} /> Nuovo impianto</button>
      </div>

      {loading ? (
        <div className="font-mono-tech text-white py-8 text-center"><Loader2 className="animate-spin inline mr-2" size={14} /> Caricamento...</div>
      ) : plants.length === 0 ? (
        <div className="border border-dashed border-[#222] p-8 text-center">
          <MapPin size={24} className="text-white mx-auto mb-3" />
          <div className="font-mono-tech text-[11px] text-white">Nessun impianto registrato per questo cliente.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {plants.map((p) => (
            <div key={p.id} className="border border-[#1a1a1a] bg-[#0a0a0a] p-5" data-testid={`plant-card-${p.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="font-display text-xl">{p.name}</div>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(p)} className="p-1.5 text-white hover:text-[#ff4b00] border border-[#1a1a1a]" data-testid={`plant-edit-${p.id}`}><Edit size={12} /></button>
                  <button onClick={() => remove(p)} className="p-1.5 text-white hover:text-red-400 border border-[#1a1a1a]" data-testid={`plant-delete-${p.id}`}><Trash2 size={12} /></button>
                </div>
              </div>
              <div className="font-mono-tech text-[10px] text-[#ff4b00] uppercase mb-3">{DIVISIONS.find((d) => d.key === p.division)?.label || p.division}</div>
              {p.address && <div className="text-white text-sm mb-1 flex items-start gap-2"><MapPin size={12} className="mt-1 shrink-0" /> {p.address}</div>}
              {p.manufacturer && <div className="text-white text-sm">{p.manufacturer} {p.model && `· ${p.model}`}</div>}
              {p.serial && <div className="font-mono-tech text-[10px] text-white mt-2">S/N: {p.serial}</div>}
              {p.install_date && <div className="font-mono-tech text-[10px] text-white mt-2">Installato: {p.install_date}</div>}
              {p.address && (
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(p.address)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost mt-4 w-full justify-center text-[11px] hover:text-[#ff4b00] hover:border-[#ff4b00]"
                  data-testid={`plant-navigate-${p.id}`}
                >
                  <Navigation size={12} /> Naviga verso l&apos;impianto
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
